import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import useLewooPayWalletAccess from "../../Hooks/useLewooPayWalletAccess";
import PassengerApi from "../../Services/Passenger";
import WalletApi from "../../Services/Wallet";
//import "./HomePage.css";

function formatAmount(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return "—";
  }

  return new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
  })
    .format(Math.round(number))
    .replace(/\u202f/g, " ");
}

function formatCurrency(value, currency = "FCFA") {
  const amount = formatAmount(value);
  return amount === "—" ? amount : `${amount} ${currency}`;
}

function formatRating(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(1)} / 5` : "0.0 / 5";
}

function clampPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.max(0, Math.min(100, number));
}

function pickText(...values) {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }

    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }

  return "";
}

function buildTimeRange(start, end) {
  const startText = pickText(start);
  const endText = pickText(end);

  if (startText && endText) {
    return `${startText} - ${endText}`;
  }

  return startText || endText || "";
}

function formatDateParts(value) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return { day: "—", month: "" };
  }

  const formatted = new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
  }).format(date);

  const [day = "—", month = ""] = formatted.split(" ");

  return {
    day,
    month: month.replace(".", ""),
  };
}

function renderRatingStars(value) {
  const filled = Math.round(Number(value) || 0);

  return Array.from({ length: 5 }, (_, index) => (index < filled ? "★" : "☆")).join("");
}

function getDisplayName(passenger, authUser, intl) {
  const passengerName = [passenger?.firstName, passenger?.lastName].filter(Boolean).join(" ").trim();
  const authName = [authUser?.firstName, authUser?.lastName].filter(Boolean).join(" ").trim();

  return (
    pickText(passenger?.displayName, passengerName, authUser?.name, authName) ||
    intl.formatMessage({ id: "dashboard.home.defaultUser" })
  );
}

function getTripOrigin(trip) {
  return pickText(
    trip?.origin?.name,
    trip?.origin?.label,
    trip?.departure?.name,
    trip?.departure?.label,
    trip?.from,
    trip?.pickupLocation,
    trip?.startLocation,
    trip?.originName,
    trip?.departureCity
  );
}

function getTripOriginNote(trip) {
  return pickText(
    trip?.origin?.address,
    trip?.departure?.address,
    trip?.fromAddress,
    trip?.pickupAddress,
    trip?.originAddress,
    trip?.startPoint,
    trip?.departureStation
  );
}

function getTripDestination(trip) {
  return pickText(
    trip?.destination?.name,
    trip?.destination?.label,
    trip?.arrival?.name,
    trip?.arrival?.label,
    trip?.to,
    trip?.dropoffLocation,
    trip?.endLocation,
    trip?.destinationName,
    trip?.arrivalCity
  );
}

function getTripDestinationNote(trip) {
  return pickText(
    trip?.destination?.address,
    trip?.arrival?.address,
    trip?.toAddress,
    trip?.dropoffAddress,
    trip?.destinationAddress,
    trip?.endPoint,
    trip?.arrivalStation
  );
}

function getTripTimeLabel(trip) {
  const timeRange = buildTimeRange(
    pickText(trip?.departureTimeLabel, trip?.departureTime, trip?.startTime, trip?.schedule?.start),
    pickText(trip?.arrivalTimeLabel, trip?.arrivalTime, trip?.endTime, trip?.schedule?.end)
  );

  return pickText(timeRange, trip?.timeLabel, trip?.scheduleLabel, trip?.departureLabel);
}

function getTripDurationLabel(trip) {
  return pickText(trip?.durationLabel, trip?.duration, trip?.travelDuration);
}

function getTripStopsLabel(trip, intl) {
  if (typeof trip?.stopsCount === "number") {
    return intl.formatMessage({ id: "dashboard.home.nextTrip.stops" }, { count: trip.stopsCount });
  }

  return pickText(trip?.stopsLabel, trip?.stops, trip?.stopLabel);
}

function getTripPriceLabel(trip) {
  return pickText(
    trip?.displayPrice,
    trip?.priceDisplay,
    trip?.amountDisplay,
    trip?.fareDisplay,
    formatCurrency(trip?.price ?? trip?.amount ?? trip?.fare ?? trip?.cost)
  );
}

function getTripServiceLabel(trip, intl) {
  return (
    pickText(trip?.serviceLabel, trip?.type, trip?.category, trip?.serviceName) ||
    intl.formatMessage({ id: "dashboard.home.nextTrip.defaultService" })
  );
}

function getBadgeTone(badge) {
  if (badge?.achieved) {
    return "green";
  }

  return (
    {
      crown: "green",
      star: "blue",
      heart: "orange",
    }[badge?.icon] || "green"
  );
}

function extractBadgeNumber(text) {
  if (typeof text !== "string") {
    return "";
  }

  const match = text.match(/(\d+(?:[.,]\d+)?)/);
  return match ? match[1] : "";
}

const BADGE_KEY_BY_ICON = {
  crown: "regularTraveler",
  star: "superPassenger",
  heart: "loyal",
};

function PlusCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="10" width="14" height="10" rx="2.5" />
      <path d="M8.5 10V8.5a3.5 3.5 0 1 1 7 0V10" />
    </svg>
  );
}

function BagIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 8h10l1 11H6L7 8Z" />
      <path d="M9 8a3 3 0 0 1 6 0" />
      <path d="M10 11h4" />
    </svg>
  );
}

function WalletIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 8.5A2.5 2.5 0 0 1 6.5 6h11A2.5 2.5 0 0 1 20 8.5V17a2 2 0 0 1-2 2H6.5A2.5 2.5 0 0 1 4 16.5v-8Z" />
      <path d="M15.5 12.5h4" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m12 4 2.6 5.2 5.8.8-4.2 4.1 1 5.8L12 17.7 6.8 20l1-5.8L3.6 10l5.8-.8L12 4Z" />
    </svg>
  );
}

function CrownIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7.5 7.5 11l4.5-5 4.5 5L20 7.5 18.5 17H5.5L4 7.5Z" />
      <path d="M6.5 17h11" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20s-7-4.5-7-9.5A4.5 4.5 0 0 1 12 7a4.5 4.5 0 0 1 7 3.5C19 15.5 12 20 12 20Z" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function TimelineLine() {
  return (
    <div className="dbc-timeline-line" aria-hidden="true">
      <span />
      <span />
    </div>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="dbc-empty-state">
      <strong>{title}</strong>
      <p>{description}</p>
    </div>
  );
}

function MetricCard({ item, locked = false, blocked = false, loading = false }) {
  const intl = useIntl();
  const isMuted = locked || blocked;

  const metaText = blocked
    ? intl.formatMessage({ id: "dashboard.home.metrics.walletRestricted" })
    : locked
      ? intl.formatMessage({ id: "dashboard.home.metrics.accountRequired" })
      : loading
        ? intl.formatMessage({ id: "dashboard.home.metrics.loading" })
        : item.meta;

  return (
    <article className={`dbc-metric-card ${isMuted ? "is-locked" : ""}`}>
      <div className={`dbc-metric-icon ${isMuted ? "is-locked" : ""}`}>
        {isMuted ? <LockIcon /> : null}
        {!isMuted && item.icon === "bag" ? <BagIcon /> : null}
        {!isMuted && item.icon === "wallet" ? <WalletIcon /> : null}
        {!isMuted && item.icon === "star" ? <StarIcon /> : null}
        {!isMuted && item.icon === "badge" ? <CrownIcon /> : null}
      </div>
      <div className="dbc-metric-copy">
        <span>{item.label}</span>
        <strong>{loading && !isMuted ? "…" : item.value ?? "—"}</strong>
        <small>{metaText}</small>
      </div>
    </article>
  );
}

function NextTripCard({ nextTrip, loading }) {
  const intl = useIntl();

  const badgeLabel = pickText(
    nextTrip?.relativeLabel,
    nextTrip?.dayLabel,
    nextTrip?.label,
    nextTrip?.statusLabel
  );

  const origin = getTripOrigin(nextTrip);
  const originNote = getTripOriginNote(nextTrip);
  const destination = getTripDestination(nextTrip);
  const destinationNote = getTripDestinationNote(nextTrip);
  const timeLabel = getTripTimeLabel(nextTrip);
  const durationLabel = getTripDurationLabel(nextTrip);
  const stopsLabel = getTripStopsLabel(nextTrip, intl);
  const hasTrip = Boolean(nextTrip && (origin || destination || timeLabel || durationLabel));

  return (
    <article className="dbc-panel">
      <div className="dbc-panel-head">
        <h3>{intl.formatMessage({ id: "dashboard.home.nextTrip.title" })}</h3>
        {badgeLabel ? <span className="dbc-pill">{badgeLabel}</span> : null}
      </div>

      {hasTrip ? (
        <>
          <div className="dbc-next-trip">
            <div className="dbc-time-col">
              <strong>{timeLabel || "—"}</strong>
              <span>{durationLabel || "—"}</span>
            </div>

            <TimelineLine />

            <div className="dbc-route-col">
              <div className="dbc-route-stop">
                <strong>{origin || "—"}</strong>
                {originNote ? <span>{originNote}</span> : null}
              </div>

              {stopsLabel ? (
                <div className="dbc-route-stops">
                  <span>{stopsLabel}</span>
                  <ChevronDownIcon />
                </div>
              ) : null}

              <div className="dbc-route-stop">
                <strong>{destination || "—"}</strong>
                {destinationNote ? <span>{destinationNote}</span> : null}
              </div>
            </div>

            <div className="dbc-time-col dbc-time-col-end">
              <strong>{pickText(nextTrip?.arrivalTimeLabel, nextTrip?.arrivalTime, nextTrip?.endTime) || "—"}</strong>
            </div>
          </div>

          <div className="dbc-panel-actions">
            <button type="button" className="dbc-outline-btn">
              {intl.formatMessage({ id: "dashboard.home.nextTrip.viewDetails" })}
            </button>
            <button type="button" className="dbc-solid-btn">
              {intl.formatMessage({ id: "dashboard.home.nextTrip.manageTrip" })}
            </button>
          </div>
        </>
      ) : (
        <EmptyState
          title={intl.formatMessage({
            id: loading ? "dashboard.home.nextTrip.loadingTitle" : "dashboard.home.nextTrip.emptyTitle",
          })}
          description={intl.formatMessage({
            id: loading ? "dashboard.home.nextTrip.loadingDescription" : "dashboard.home.nextTrip.emptyDescription",
          })}
        />
      )}
    </article>
  );
}

function WalletCard({ blocked = false, locked = false, status = "checking", balance = null, balanceLoading = false }) {
  const intl = useIntl();
  const isDisabled = locked || blocked;
  const balanceDisplay = balanceLoading ? "…" : balance !== null ? formatAmount(balance) : "—";

  const statusKey = blocked
    ? "blocked"
    : status === "pending"
      ? "pending"
      : status === "approved-needs-creation"
        ? "needsCreation"
        : status === "error"
          ? "error"
          : locked
            ? "checking"
            : "available";

  const statusCopy = {
    eyebrow: intl.formatMessage({ id: `dashboard.home.wallet.${statusKey}.eyebrow` }),
    value:
      statusKey === "available"
        ? balanceDisplay
        : intl.formatMessage({ id: `dashboard.home.wallet.${statusKey}.value` }),
    note: intl.formatMessage({ id: `dashboard.home.wallet.${statusKey}.note` }),
  };

  return (
    <article className={`dbc-panel dbc-wallet-panel ${blocked ? "is-muted" : ""}`.trim()}>
      <div className="dbc-panel-head">
        <h3>{intl.formatMessage({ id: "dashboard.home.wallet.title" })}</h3>
        {isDisabled ? (
          <span className="dbc-wallet-locked-chip">
            <LockIcon />
            {intl.formatMessage({
              id: blocked ? "dashboard.home.wallet.restrictedChip" : "dashboard.home.wallet.lockedChip",
            })}
          </span>
        ) : (
          <a href="/">{intl.formatMessage({ id: "dashboard.home.wallet.viewAll" })}</a>
        )}
      </div>

      <div className={`dbc-wallet-hero ${isDisabled ? "is-locked" : ""}`}>
        <div>
          <span>{statusCopy.eyebrow}</span>
          <strong>{isDisabled ? statusCopy.value : balanceDisplay}</strong>
          <small>{statusCopy.note}</small>
        </div>

        <div className={`dbc-wallet-art ${isDisabled ? "is-locked" : ""}`} aria-hidden="true">
          <div className="dbc-wallet-outline" />
          {isDisabled ? (
            <div className="dbc-wallet-lock" aria-hidden="true">
              <LockIcon />
            </div>
          ) : null}
        </div>
      </div>

      {isDisabled ? (
        <p className="dbc-wallet-lock-note">
          <LockIcon />
          <span>{statusCopy.note}</span>
        </p>
      ) : null}

      <div className={`dbc-wallet-actions ${isDisabled ? "is-locked" : ""}`}>
        <button type="button" disabled={isDisabled}>
          <span className="dbc-mini-circle">{isDisabled ? <LockIcon /> : "+"}</span>
          {intl.formatMessage({ id: "dashboard.home.wallet.addMoney" })}
        </button>
        <button type="button" disabled={isDisabled}>
          <span className="dbc-mini-circle">{isDisabled ? <LockIcon /> : "↗"}</span>
          {intl.formatMessage({ id: "dashboard.home.wallet.withdraw" })}
        </button>
        <button type="button" disabled={isDisabled}>
          <span className="dbc-mini-circle">{isDisabled ? <LockIcon /> : "◌"}</span>
          {intl.formatMessage({ id: "dashboard.home.wallet.history" })}
        </button>
      </div>
    </article>
  );
}

function BadgesCard({ badges, loading }) {
  const intl = useIntl();
  const hasBadges = Array.isArray(badges) && badges.length > 0;

  return (
    <article className="dbc-panel">
      <div className="dbc-panel-head">
        <h3>{intl.formatMessage({ id: "dashboard.home.badges.title" })}</h3>
        <a href="/">{intl.formatMessage({ id: "dashboard.home.badges.viewAll" })}</a>
      </div>

      {hasBadges ? (
        <div className="dbc-badges">
          {badges.map((badge) => {
            const badgeKey = BADGE_KEY_BY_ICON[badge.icon];
            const rawDescription = pickText(badge.description, badge.desc);
            const count = extractBadgeNumber(rawDescription);

            const label = badgeKey
              ? intl.formatMessage({ id: `dashboard.home.badges.${badgeKey}.label` })
              : pickText(badge.label, badge.title) || intl.formatMessage({ id: "dashboard.home.badges.defaultLabel" });

            const description = badgeKey
              ? intl.formatMessage({ id: `dashboard.home.badges.${badgeKey}.description` }, { count })
              : rawDescription || intl.formatMessage({ id: "dashboard.home.badges.defaultDescription" });

            return (
              <div key={badge.id || badge.label || badge.title} className="dbc-badge-row">
                <div className={`dbc-badge-icon is-${getBadgeTone(badge)}`}>
                  {badge.icon === "crown" ? <CrownIcon /> : null}
                  {badge.icon === "star" ? <StarIcon /> : null}
                  {badge.icon === "heart" ? <HeartIcon /> : null}
                </div>
                <div>
                  <strong>{label}</strong>
                  <span>{description}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title={intl.formatMessage({
            id: loading ? "dashboard.home.badges.loadingTitle" : "dashboard.home.badges.emptyTitle",
          })}
          description={intl.formatMessage({
            id: loading ? "dashboard.home.badges.loadingDescription" : "dashboard.home.badges.emptyDescription",
          })}
        />
      )}
    </article>
  );
}

function HistoryCard({ trips, loading }) {
  const intl = useIntl();
  const hasTrips = Array.isArray(trips) && trips.length > 0;

  return (
    <article className="dbc-panel">
      <div className="dbc-panel-head">
        <h3>{intl.formatMessage({ id: "dashboard.home.history.title" })}</h3>
        <a href="/">{intl.formatMessage({ id: "dashboard.home.history.viewAll" })}</a>
      </div>

      {hasTrips ? (
        <div className="dbc-history-list">
          {trips.map((trip, index) => {
            const dateParts = formatDateParts(trip.completedAt || trip.date || trip.createdAt || trip.tripDate || trip.travelDate);
            const origin = getTripOrigin(trip);
            const destination = getTripDestination(trip);
            const timeLabel = getTripTimeLabel(trip);
            const priceLabel = getTripPriceLabel(trip);
            const serviceLabel = getTripServiceLabel(trip, intl);

            return (
              <div key={trip.id || `${origin}-${destination}-${index}`} className="dbc-history-row">
                <div className="dbc-history-date">
                  <strong>{dateParts.day}</strong>
                  <span>{dateParts.month}</span>
                </div>
                <div className="dbc-history-route">
                  <strong>
                    {origin || "—"} → {destination || "—"}
                  </strong>
                  {timeLabel ? <span>{timeLabel}</span> : null}
                </div>
                <div className="dbc-history-price">
                  <strong>{priceLabel}</strong>
                  <span>{serviceLabel}</span>
                </div>
                <button type="button" className="dbc-evaluate-btn">
                  {intl.formatMessage({ id: "dashboard.home.history.evaluate" })}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title={intl.formatMessage({
            id: loading ? "dashboard.home.history.loadingTitle" : "dashboard.home.history.emptyTitle",
          })}
          description={intl.formatMessage({
            id: loading ? "dashboard.home.history.loadingDescription" : "dashboard.home.history.emptyDescription",
          })}
        />
      )}
    </article>
  );
}

function DestinationsCard({ destinations, loading }) {
  const intl = useIntl();
  const hasDestinations = Array.isArray(destinations) && destinations.length > 0;

  return (
    <article className="dbc-panel">
      <div className="dbc-panel-head">
        <h3>{intl.formatMessage({ id: "dashboard.home.destinations.title" })}</h3>
        <a href="/">{intl.formatMessage({ id: "dashboard.home.destinations.viewAll" })}</a>
      </div>

      {hasDestinations ? (
        <div className="dbc-progress-list">
          {destinations.map((destination, index) => {
            const destinationName = pickText(
              destination.name,
              destination.label,
              destination.destination,
              destination.city,
              destination.title
            );
            const percentValue = clampPercent(
              destination.percentage ?? destination.percent ?? destination.value ?? destination.progress ?? 0
            );
            const displayPercent = pickText(destination.displayPercentage, `${Math.round(percentValue)}%`);

            return (
              <div key={destination.id || destinationName || index} className="dbc-progress-row">
                <div className="dbc-progress-label">
                  <span className="dbc-dot" />
                  <strong>
                    {destinationName || intl.formatMessage({ id: "dashboard.home.destinations.defaultLabel" })}
                  </strong>
                </div>
                <div className="dbc-progress-bar">
                  <span style={{ width: `${percentValue}%` }} />
                </div>
                <strong className="dbc-progress-value">{displayPercent}</strong>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title={intl.formatMessage({
            id: loading ? "dashboard.home.destinations.loadingTitle" : "dashboard.home.destinations.emptyTitle",
          })}
          description={intl.formatMessage({
            id: loading
              ? "dashboard.home.destinations.loadingDescription"
              : "dashboard.home.destinations.emptyDescription",
          })}
        />
      )}
    </article>
  );
}

function RatingsCard({ ratings, loading }) {
  const intl = useIntl();
  const distribution = Array.isArray(ratings?.distribution) ? ratings.distribution : [];
  const hasRatings = distribution.length > 0;
  const averageValue = ratings?.displayAverage || formatRating(ratings?.average);
  const countValue = intl.formatMessage(
    { id: "dashboard.home.ratings.countLabel" },
    { count: formatAmount(ratings?.count ?? 0) }
  );
  const stars = renderRatingStars(ratings?.average ?? 0);

  return (
    <article className="dbc-panel">
      <div className="dbc-panel-head">
        <h3>{intl.formatMessage({ id: "dashboard.home.ratings.title" })}</h3>
        <a href="/">{intl.formatMessage({ id: "dashboard.home.ratings.viewAll" })}</a>
      </div>

      {hasRatings ? (
        <>
          <div className="dbc-rating-summary">
            <strong>{averageValue}</strong>
            <span>{countValue}</span>
            <div className="dbc-stars">{stars}</div>
          </div>
          <div className="dbc-rating-bars">
            {distribution.map((rating) => {
              const percentValue = clampPercent(rating.percentage ?? 0);

              return (
                <div key={rating.star} className="dbc-rating-row">
                  <span>{rating.star} ★</span>
                  <div className="dbc-progress-bar">
                    <span style={{ width: `${percentValue}%` }} />
                  </div>
                  <strong>{pickText(rating.displayCount, rating.count ?? 0)}</strong>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <EmptyState
          title={intl.formatMessage({
            id: loading ? "dashboard.home.ratings.loadingTitle" : "dashboard.home.ratings.emptyTitle",
          })}
          description={intl.formatMessage({
            id: loading ? "dashboard.home.ratings.loadingDescription" : "dashboard.home.ratings.emptyDescription",
          })}
        />
      )}
    </article>
  );
}

export default function DashboardClient() {
  const intl = useIntl();
  const { walletLocked, walletStatus } = useLewooPayWalletAccess();
  const lewooPayToken = useSelector(
    (state) => state?.AuthReducer?.lewooPayToken || localStorage.getItem("lewooPayToken") || ""
  );
  const authUser = useSelector((state) => state?.AuthReducer?.user || {});
  const resolvedToken = String(lewooPayToken || "").trim();

  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState("");
  const [walletBalance, setWalletBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [totalDepenses, setTotalDepenses] = useState(null);
  const [walletAccessBlocked, setWalletAccessBlocked] = useState(false);

  useEffect(() => {
    let isActive = true;

    const loadDashboard = async () => {
      setDashboardLoading(true);
      setDashboardError("");

      try {
        const response = await PassengerApi.getPassengerDashboard();
        if (!isActive) {
          return;
        }

        setDashboardData(response?.data?.data || null);
      } catch (error) {
        if (!isActive) {
          return;
        }

        setDashboardError(
          error?.response?.data?.message || intl.formatMessage({ id: "dashboard.home.hero.defaultError" })
        );
      } finally {
        if (isActive) {
          setDashboardLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadWalletSummary = async () => {
      if (!resolvedToken) {
        setWalletAccessBlocked(false);
        setWalletBalance(null);
        setTotalDepenses(null);
        setBalanceLoading(false);
        return;
      }

      setBalanceLoading(true);
      setWalletAccessBlocked(false);
      setWalletBalance(null);
      setTotalDepenses(null);

      try {
        const walletsResponse = await WalletApi.getClientWallets(resolvedToken, {
          suppressForbiddenRedirect: true,
        });

        if (!isActive) {
          return;
        }

        const wallets = walletsResponse?.data?.data || [];
        const primary = wallets.find((wallet) => wallet.is_primary) || wallets[0] || null;

        if (!primary) {
          setWalletBalance(0);
          setTotalDepenses(0);
          return;
        }

        const walletId = primary.id;
        const syncResponse = await WalletApi.syncClientWallet(walletId, resolvedToken, {
          suppressForbiddenRedirect: true,
        });

        if (!isActive) {
          return;
        }

        const data = syncResponse?.data?.data || {};
        setWalletBalance(data.balance ?? 0);

        const transactionsResponse = await WalletApi.getClientWalletTransactions(
          walletId,
          resolvedToken,
          {},
          {
            suppressForbiddenRedirect: true,
          }
        );

        if (!isActive) {
          return;
        }

        const txs = transactionsResponse?.data?.data || [];
        const total = txs
          .filter((transaction) => transaction.debit === true || transaction.entryType === "DEBIT")
          .reduce((sum, transaction) => sum + (parseFloat(transaction.amount) || 0), 0);

        setTotalDepenses(total);
      } catch (error) {
        if (!isActive) {
          return;
        }

        if (error?.response?.status === 403) {
          setWalletAccessBlocked(true);
          setWalletBalance(null);
          setTotalDepenses(null);
        }
      } finally {
        if (isActive) {
          setBalanceLoading(false);
        }
      }
    };

    loadWalletSummary();

    return () => {
      isActive = false;
    };
  }, [resolvedToken]);

  const passenger = dashboardData?.passenger || authUser || {};
  const periodLabel = intl.formatMessage({ id: "dashboard.home.period.default" });
  const cards = dashboardData?.cards || {};
  const completedTrips = cards.completedTrips || {};
  const averageRating = cards.averageRating || {};
  const loyaltyPoints = cards.loyaltyPoints || {};
  const topDestinations = Array.isArray(dashboardData?.topDestinations) ? dashboardData.topDestinations : [];
  const tripHistory = Array.isArray(dashboardData?.tripHistory) ? dashboardData.tripHistory : [];
  const badges = Array.isArray(dashboardData?.badges) ? dashboardData.badges : [];
  const nextTrip = dashboardData?.nextTrip || null;
  const ratings = dashboardData?.ratings || {};

  const displayName = getDisplayName(passenger, authUser, intl);

  const metaWithPeriod = (value) =>
    intl.formatMessage({ id: "dashboard.home.metrics.metaWithPeriod" }, { value, period: periodLabel });

  const metricCards = [
    {
      label: intl.formatMessage({ id: "dashboard.home.metrics.completedTrips.title" }),
      value: completedTrips.displayValue || (dashboardLoading ? "…" : "0"),
      meta:
        completedTrips.subtitle ||
        (dashboardLoading ? intl.formatMessage({ id: "dashboard.home.metrics.loading" }) : metaWithPeriod("0 %")),
      icon: "bag",
      loading: dashboardLoading,
    },
    {
      label: intl.formatMessage({ id: "dashboard.home.metrics.totalExpenses.title" }),
      value:
        totalDepenses !== null
          ? formatCurrency(totalDepenses)
          : walletAccessBlocked || walletLocked
            ? "—"
            : balanceLoading
              ? "…"
              : "—",
      meta: walletAccessBlocked
        ? intl.formatMessage({ id: "dashboard.home.metrics.walletRestricted" })
        : walletLocked
          ? intl.formatMessage({ id: "dashboard.home.metrics.accountRequired" })
          : balanceLoading
            ? intl.formatMessage({ id: "dashboard.home.metrics.totalExpenses.syncing" })
            : intl.formatMessage({ id: "dashboard.home.metrics.totalExpenses.managedByWallet" }),
      icon: "wallet",
      locked: walletLocked,
      blocked: walletAccessBlocked,
      loading: balanceLoading,
    },
    {
      label: intl.formatMessage({ id: "dashboard.home.metrics.averageRating.title" }),
      value: averageRating.displayValue || (dashboardLoading ? "…" : "0.0 / 5"),
      meta:
        averageRating.subtitle ||
        (dashboardLoading ? intl.formatMessage({ id: "dashboard.home.metrics.loading" }) : metaWithPeriod("0.0")),
      icon: "star",
      loading: dashboardLoading,
    },
    {
      label: intl.formatMessage({ id: "dashboard.home.metrics.loyaltyPoints.title" }),
      value: loyaltyPoints.displayValue || (dashboardLoading ? "…" : "0 pts"),
      meta:
        loyaltyPoints.subtitle ||
        (dashboardLoading ? intl.formatMessage({ id: "dashboard.home.metrics.loading" }) : metaWithPeriod("0")),
      icon: "badge",
      loading: dashboardLoading,
    },
  ];

  return (
    <>
      <div className="dbc-hero">
        <div>
          <span className="dbc-hero-kicker">{intl.formatMessage({ id: "dashboard.home.hero.kicker" })}</span>
          <h1>{intl.formatMessage({ id: "dashboard.home.hero.greeting" }, { name: displayName })}</h1>
          <p>{intl.formatMessage({ id: "dashboard.home.hero.subtitle" }, { period: periodLabel })}</p>
          {dashboardLoading ? (
            <small className="dbc-hero-hint">{intl.formatMessage({ id: "dashboard.home.hero.loadingHint" })}</small>
          ) : null}
          {dashboardError ? <div className="dbc-dashboard-notice is-error">{dashboardError}</div> : null}
        </div>
        <button type="button" className="dbc-reserve-btn">
          {intl.formatMessage({ id: "dashboard.home.hero.reserveTrip" })}
          <PlusCircleIcon />
        </button>
      </div>

      <div className="dbc-metrics">
        {metricCards.map((item) => (
          <MetricCard
            key={item.label}
            item={item}
            locked={Boolean(item.locked)}
            blocked={Boolean(item.blocked)}
            loading={Boolean(item.loading)}
          />
        ))}
      </div>

      <div className="dbc-middle-grid">
        <NextTripCard nextTrip={nextTrip} loading={dashboardLoading} />
        <WalletCard
          blocked={walletAccessBlocked}
          locked={walletLocked}
          status={walletStatus}
          balance={walletBalance}
          balanceLoading={balanceLoading}
        />
        <BadgesCard badges={badges} loading={dashboardLoading} />
      </div>

      <div className="dbc-bottom-grid">
        <HistoryCard trips={tripHistory} loading={dashboardLoading} />
        <DestinationsCard destinations={topDestinations} loading={dashboardLoading} />
        <RatingsCard ratings={ratings} loading={dashboardLoading} />
      </div>
    </>
  );
}